import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import authService from './api-authorization/AuthorizeService';
import * as DateHelpers from './DateHelpers';
import NotesPopup from './NotesPopup';
import SchedulePickupConfirmData from './SchedulePickupConfirm';

const win: any = window;
const Stimulsoft = win.Stimulsoft;

interface SchedulePickup {
    jobIdSearch: string;
    siteAddressSearch: string;
    customerSearch: string;
    showResults: boolean;
    results: AddNewResults[];
    showEditNotesModal: boolean;
    editNotes: EditNotes;
    sendEmailToCustomer: boolean;
    truckOptions: GenericList[];
    yesNoOptions: GenericList[];
    tiltSlideClearance: number;
    plumbingElectricityDisconnected: number;
    disconnectionDate: string;
    pickupTravelPriceNote: string;
    pickupTravelExtraHoursPriceNote: string;
    weekdayAccess: number;
    phoneCallRequired: number;
    fencePartPickup: number;
    fencePartPickupPanels: number;
    fencingAttachments: boolean;
    fencingAttachmentsDescription: string;
    jobPickedUp: number;
    createFinalInvoice: boolean;
    jobPickedUpNotes: string;
    fencePartPickupFee: number;
    fencePartPickupFeeFormatted: string;
    showPickupConfirm: boolean;
    pickupConfirmJobIds: ConfirmJobIdList[];
    saveDisabled: boolean;
    loading: boolean;
    errors: any;
}

interface AddNewResults {
    itemIndex: number;
    selected: boolean;
    isEws: boolean;
    jobQuoteItemId: number;
    jobId: number;
    siteAddress: string;
    customerId: number;
    customer: string;
    customerCreditCard: boolean;    //true if the customer is prepaid and paying by credit card
    customerDirectDebit: boolean;   //true if the customer is prepaid and paying by credit card
    customerSendConfirmationEmail: boolean;    //false if customer isn't setup for emails
    purchaseOrderNumber: string;
    item: string;
    itemId: number;
    itemTypeId: number;
    itemShortcut: string;
    type: string;
    quantityTotal: number;
    quantityScheduledAvailable: number;     //cater for part pickups
    quantityScheduled: number;
    quantityType: string;
    hirePeriod: string;
    hirePeriodActual: number;
    chargePublicHols: boolean;
    shutdownPeriodType: number;
    offhireDate: string;
    endhireDate: string;
    pickupDate: string;
    bringForwardDate: string;
    bringForwardDateFormatted: string;
    pickupScheduled: boolean;
    callout: boolean;
    allowOnGoingHire: boolean;
    truck: number;
    canChangeTruck: boolean;
    notes: string;
}

interface EditNotes {
    heading: string;
    rowIndex: number;
    parentIndex: number;
    id: number;
    itemId: number;
    item: string;
    notes: string;
}

interface GenericList {
    id: number;
    name: string;
}

interface ConfirmJobIdList {
    jobId: number;
    hasInstall: boolean;    //at least one install for this job - need to show payment method for installs only (prepaid customer)
}

export class SchedulePickupData extends React.Component<RouteComponentProps<{}>, SchedulePickup> {
    constructor(props: any) {
        super(props);

        this.state = {
            loading: true,
            jobIdSearch: "",
            siteAddressSearch: "",
            customerSearch: "",
            showResults: false,
            results: [],
            showEditNotesModal: false,
            editNotes: {
                heading: "",
                parentIndex: 0,
                rowIndex: 0,
                id: 0,
                itemId: 0,
                item: "",
                notes: ""
            },
            sendEmailToCustomer: true,
            truckOptions: [],
            yesNoOptions: [],
            tiltSlideClearance: -1,
            plumbingElectricityDisconnected: -1,
            disconnectionDate: "",
            pickupTravelPriceNote: "",
            pickupTravelExtraHoursPriceNote: "",
            weekdayAccess: -1,
            phoneCallRequired: -1,
            jobPickedUp: -1,
            fencePartPickup: -1,
            fencePartPickupPanels: 0,
            fencingAttachments: false,
            fencingAttachmentsDescription: "",
            createFinalInvoice: true,
            jobPickedUpNotes: "",
            fencePartPickupFee: 0,
            fencePartPickupFeeFormatted: "$0.00",
            showPickupConfirm: false,
            pickupConfirmJobIds: [],
            saveDisabled: false,
            errors: {}
        }
    }

    componentDidMount() {
        this.getData();
    }

    async getData() {
        const token = await authService.getAccessToken();

        axios.get('api/Schedule/GetPickupData', {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            this.setState({ truckOptions: res.data.truckOptions, yesNoOptions: res.data.yesNo, pickupTravelPriceNote: res.data.pickupTravelPriceNote, pickupTravelExtraHoursPriceNote: res.data.pickupTravelExtraHoursPriceNote, loading: false });
            
        })
        .catch(error => {
            toast.error(error.message);
        });       
    }

    handleChange = (e: any) => {
        //check if there is a value and an error, and if so remove from error list
        var errors = this.state.errors;
        if (e.target.value) {
            delete errors[e.target.name];
        }

        let change: any = {};
        change[e.target.name] = e.target.value;
        change["errors"] = errors;
        this.setState(change);
    }

    handleChangeNumber = (e: any) => {
        //check if there is a value and an error, and if so remove from error list
        var errors = this.state.errors;
        if (e.target.value) {
            delete errors[e.target.name];
        }

        let change: any = {};
        change[e.target.name] = e.target.value * 1;
        change["errors"] = errors;
        this.setState(change);
    }

    //grid item changed
    onCellChange = (e: any, index: number) => {
        //update item that has changed
        var results = this.state.results;
        var result = results[index];
        let newItem: any = {};
        newItem = result;
        newItem[e.target.name] = e.target.value;

        this.setState({ results: results, errors: {} });
    }

    onCellTruckChange = (e: any, index: number) => {
        //update item that has changed
        var results = this.state.results;
        var result = results[index];
        let newItem: any = {};
        newItem = result;
        newItem[e.target.name] = e.target.value * 1;

        this.setState({ results: results, tiltSlideClearance: -1, errors: {} });
    }

    onKeyUp = (e: any) => {
        //check if enter key has been pressed and if so call search
        if (e.keyCode === 13) {
            this.search(e);
        }
    }

    handleJobPickedUpChange = (e: any) => {
        var errors = this.state.errors;
        if (e.target.value) {
            delete errors[e.target.name];
        }

        var value = e.target.value * 1;

        var wkDayAccess = -1;
        var phCallReq = -1;
        if (value == 1) {
            //default weekday access and phone call
            wkDayAccess = 1;
            phCallReq = 0;
        }

        this.setState({ errors: errors, jobPickedUp: value, weekdayAccess: wkDayAccess, phoneCallRequired: phCallReq, createFinalInvoice: true });
    }

    handleFinalInvoiceCheckboxChange = (e: any) => {
        this.setState({ createFinalInvoice: e.target.checked });
    }

    handlejobPickedUpNotesChange = (e: any) => {
        var errors = this.state.errors;
        if (e.target.value) {
            delete errors[e.target.name];
        }

        this.setState({ errors: errors, jobPickedUpNotes: e.target.value });
    }

    handlePartPickupChange = (e: any) => {
        var errors = this.state.errors;
        if (e.target.value) {
            delete errors[e.target.name];
        }

        var value = e.target.value * 1;

        if (value === 1) {
            //part pickup so need to get the callout fee that the customer will be charged
            this.getPartPickupFee(value);
        } else {
            this.setState({ fencePartPickup: value, fencePartPickupPanels: 0 });
        }
    }

    getPartPickupFee = async (partPickup: number) => {
        const token = await authService.getAccessToken();

        var items = this.state.results.filter(r => r.selected);
        if (items.length > 0) {
            var firstItem = items[0];       //the items should be for the same job / same customer so just get the first one

            axios.get('api/Schedule/GetFencePartPickupFee?CustomerId=' + firstItem.customerId, {
                headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
            })
            .then(res => {
                this.setState({ fencePartPickupFee: res.data.fencePartPickupFee, fencePartPickupFeeFormatted: res.data.fencePartPickupFeeFormatted, fencePartPickup: partPickup, fencePartPickupPanels: 0 });
            })
            .catch(error => {
                toast.error(error.message);
            });
        }
    }

    handleFencingAttachmentsChange = (e: any) => {
        var errors = this.state.errors;
        if (e.target.value) {
            delete errors[e.target.name];
        }

        this.setState({ fencingAttachments: e.target.checked, fencingAttachmentsDescription: "" });
    }

    handleSendEmailCheckboxChange = (e: any) => {
        this.setState({ sendEmailToCustomer: e.target.checked });
    }

    handleTruckClearanceChange = (e: any) => {
        var errors = this.state.errors;
        if (e.target.value) {
            delete errors[e.target.name];
        }

        var value = e.target.value * 1;

        this.setState({ tiltSlideClearance: value, errors: errors });

        if (value === 0) {
            //if there isn't enough clearance then we need to change the trucks to crane for selected jobs

            //find crane truck
            var craneTruck = this.state.truckOptions.filter(t => t.name.toLowerCase() === 'crane');
            if (craneTruck.length > 0) {
                let craneTruckId = craneTruck[0].id;

                //update trucks to crane for every item
                var resultList = this.state.results;
                var filteredList = resultList.filter(r => r.selected);
                for (var i = 0; i < filteredList.length; i++) {
                    let item = filteredList[i];

                    item.truck = craneTruckId; 
                }
                this.setState({ results: resultList });
            }
        }
    }

    search = (e: any) => {
        e.preventDefault();

        if (!this.state.jobIdSearch && !this.state.siteAddressSearch && !this.state.customerSearch) {
            toast.error("Please enter a Search criteria before searching!");
        } else {
            this.resetQuestions();
            this.searchJobs();
        }
    }

    resetQuestions = () => {
        //reset the answers to the questions if we search again
        this.setState({
            jobPickedUp: -1,
            createFinalInvoice: true,
            jobPickedUpNotes: "",
            tiltSlideClearance: -1,
            plumbingElectricityDisconnected: -1,
            disconnectionDate: "",
            pickupTravelPriceNote: "",
            pickupTravelExtraHoursPriceNote: "",
            fencePartPickup: - 1,
            fencePartPickupPanels: 0,
            fencePartPickupFee: 0,
            fencePartPickupFeeFormatted: "$0.00",
            fencingAttachments: false,
            fencingAttachmentsDescription: "",
            weekdayAccess: -1,
            phoneCallRequired: -1
        });
    }

    async searchJobs() {
        const token = await authService.getAccessToken();

        axios.get('api/Schedule/GetPickupItemsForScheduling?JobId=' + this.state.jobIdSearch + '&SiteAddress=' + this.state.siteAddressSearch + '&Customer=' + this.state.customerSearch, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                };
            } else {
                var items = res.data.items;
                for (var i = 0; i < items.length; i++) {
                    items[i].itemIndex = i;
                };
                this.setState({ showResults: true, results: items })
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    selectItem = (e: any, index: number) => {
        var results = this.state.results;
        var result = results[index];
        result.selected = e.target.checked;

        var customersNoEmail = results.filter(r => !r.customerSendConfirmationEmail && r.selected);
        var canSendEmail = customersNoEmail.length === 0;

        this.setState({ results: results, sendEmailToCustomer: canSendEmail });
    }

    //DEV ONLY
    selectJobs = (e: any) => {
        e.preventDefault();

        var results = this.state.results;
        for (var i = 0; i < results.length; i++) {
            results[i].selected = true;
            var today = DateHelpers.getCurrentDate();
            results[i].pickupDate = today;
        }

        this.setState({ results: results });
    }

    //Notes Popup 
    editNotes = (e: any, itemIndex: number) => {
        e.preventDefault();

        var results = this.state.results;
        var item = results[itemIndex];

        var editNotes: EditNotes = {
            heading: "",
            parentIndex: 0,
            rowIndex: itemIndex,
            id: 0,
            itemId: item.itemId,
            item: item.item,
            notes: item.notes
        }

        this.setState({ editNotes: editNotes, showEditNotesModal: true });
    }

    hideNotes = () => {
        var editNotes: EditNotes = {
            heading: "",
            parentIndex: 0,
            rowIndex: 0,
            id: 0,
            itemId: 0,
            item: "",
            notes: ""
        }

        this.setState({ editNotes: editNotes, showEditNotesModal: false });
    }

    updateNotes = (editNote: EditNotes) => {
        var editNotes = this.state.editNotes;
        editNotes.notes = editNote.notes;
        this.setState({
            editNotes: editNotes
        });
    }

    saveNotes = () => {
        this.saveItemNotes();
    }

    async saveItemNotes() {
        //save notes
        var data = this.state.editNotes;
        var results = this.state.results;
        var item = results[data.rowIndex];
        item.notes = data.notes;

        //reset
        var editNotes: EditNotes = {
            heading: "",
            parentIndex: 0,
            rowIndex: 0,
            id: 0,
            itemId: 0,
            item: "",
            notes: ""
        }

        this.setState({ results: results, editNotes: editNotes, showEditNotesModal: false });
    }

    //End Notes Popup

    validate = (jobs: AddNewResults[]) => {
        let errors: any = {};
        let formIsValid = true;
        var rowNumber = 0;
        var customerId = 0;

        var errorString = "Please enter the following values: ";
        var error = false;

        for (var i = 0; i < jobs.length; i++) {
            rowNumber += 1;
            var newErrorString = "";

            //check to make sure they are scheduling jobs for the same customer as I need to send an email to the customer with the pickup details
            if (i === 0) {
                customerId = jobs[i].customerId;
            } else if (customerId != jobs[i].customerId) {
                formIsValid = false;
                errors["multipleJobIds"] = "Pickups MUST be for the SAME customer!";
            }

            if (jobs[i].truck < 1 && jobs[i].isEws && jobs[i].itemTypeId != 21) {
                newErrorString += " Truck, ";
                error = true;
            }

            if (!jobs[i].pickupDate) {
                newErrorString += " Pick Up Date, ";
                error = true;
            } else if (!DateHelpers.isValidDate(jobs[i].pickupDate)) {
                newErrorString += " Valid Pick Up Date, ";
                error = true;
            }

            //if an offhire date is entered, validate it
            if (jobs[i].offhireDate && !DateHelpers.isValidDate(jobs[i].offhireDate)) {
                newErrorString += " Valid Offhire Date, ";
                error = true;
            } else if (jobs[i].offhireDate && !DateHelpers.compareDate(jobs[i].offhireDate, jobs[i].pickupDate)) {
                newErrorString += " Offhire Date MUST be <= Pick Up Date, ";
                error = true;
            }

            var qtyScheduled = jobs[i].quantityScheduled * 1;
            var qtyScheduledAvail = jobs[i].quantityScheduledAvailable * 1;
            if (qtyScheduled <= 0 || (qtyScheduled > qtyScheduledAvail)) {
                newErrorString += " Valid Quantity, ";
                error = true;
            }

            if (jobs[i].bringForwardDate && !DateHelpers.isValidDate(jobs[i].bringForwardDate)) {
                newErrorString += " Valid Bring Forward Date, ";
                error = true;
            }

            if (newErrorString != "") {
                errorString += "\n";
                errorString += "Row " + (jobs[i].itemIndex + 1) + ": " + newErrorString;
                errorString = errorString.substring(0, errorString.length - 2);
            }
        }

        if (this.state.jobPickedUp < 0) {
            formIsValid = false;
            errors["jobPickedUp"] = "Item Already Picked Up is required";
        }

        if (this.state.jobPickedUp === 1 && this.state.jobPickedUpNotes.trim() === "") {
            formIsValid = false;
            errors["jobPickedUpNotes"] = "Notes are required";
        }

        if (this.state.jobPickedUp < 1) {
            //only answer questions if the job hasn't been picked up from site already
            //these are only used to help the drivers
            let isEwsItem = jobs.filter(q => q.isEws && q.itemTypeId != 21).length > 0;
            let isTiltSlide = jobs.filter(q => q.truck === 1).length > 0;
            if (isEwsItem) {
                if (isTiltSlide && this.state.tiltSlideClearance < 0) {
                    formIsValid = false;
                    errors["tiltSlideClearance"] = "Tilt Slide Clearance is required!";
                }
                if (this.state.plumbingElectricityDisconnected < 0) {
                    formIsValid = false;
                    errors["plumbingElectricityDisconnected"] = "Plumbing Electricity Disconnected is required!";
                }
            }
            let isPowerPole = jobs.filter(q => q.itemTypeId === 23).length > 0;
            if (isPowerPole) {
                if (this.state.plumbingElectricityDisconnected < 0) {
                    formIsValid = false;
                    errors["plumbingElectricityDisconnected"] = "Electricity Disconnected is required!";
                } else if (this.state.plumbingElectricityDisconnected === 0) {
                    formIsValid = false;
                    errors["plumbingElectricityDisconnected"] = "Customer MUST get electricity disconnected before scheduling pick up!";
                } else if (this.state.plumbingElectricityDisconnected === 1) {
                    //need disconnection date
                    if (!this.state.disconnectionDate) {
                        formIsValid = false;
                        errors["disconnectionDate"] = "Disconnection Date is required!";
                    }
                }
            }

            let isFencing = jobs.filter(q => q.itemTypeId === 50).length > 0;
            if (isFencing) {
                if (this.state.fencePartPickup < 0) {
                    formIsValid = false;
                    errors["fencePartPickup"] = "Is Fence Part Pickup is required!";
                }

                if (this.state.fencingAttachments && !this.state.fencingAttachmentsDescription) {
                    formIsValid = false;
                    errors["fencingAttachmentsDescription"] = "Fencing Items are required!";
                }
            }
            
            if (this.state.weekdayAccess < 0) {
                formIsValid = false;
                errors["weekdayAccess"] = "Weekday Access is required";
            }

            if (this.state.phoneCallRequired < 0) {
                formIsValid = false;
                errors["phoneCallRequired"] = "Phone Call is required";
            }
        }

        if (error) {
            formIsValid = false;
            errors["scheduleItems"] = errorString;
        }
        this.setState({ errors: errors });
        return formIsValid;
    }

    schedule = () => {
        if (this.state.saveDisabled) {
            return;
        }
        this.setState({ saveDisabled: true });

        var jobs = this.state.results.filter(j => j.selected);

        if (jobs.length === 0) {
            this.setState({ saveDisabled: false });
            toast.error("Please select job/s to schedule a pickup");
        } else {
            if (this.validate(jobs)) {
                if (this.state.jobPickedUp === 1) {
                    //we don't need to confirm the contact
                    this.scheduleJobs();
                } else {
                    //show confirm contacts screen for each job that is being scheduled (should just be one job)
                    //add jobs ids to list
                    var jobIds: ConfirmJobIdList[] = [];
                    for (var i = 0; i < jobs.length; i++) {
                        var job = jobs[i];
                        var existJobId = jobIds.filter(j => j.jobId == job.jobId);
                        if (existJobId.length === 0) {
                            //not added yet so add
                            var jobIdToAdd: ConfirmJobIdList = {
                                jobId: job.jobId,
                                hasInstall: true
                            };
                            jobIds.push(jobIdToAdd);
                        }
                    }

                    this.setState({ showPickupConfirm: true, pickupConfirmJobIds: jobIds });
                }
            } else {
                this.setState({ saveDisabled: false });
                toast.error("Please fix validation errors on screen!");
            }
            
        }
    }

    //confirm job contact/s before scheduling pickup
    hideConfirmPickup = () => {
        this.setState({ pickupConfirmJobIds: [], showPickupConfirm: false, saveDisabled: false });
    }

    saveConfirmPickup = () => {
        this.scheduleJobs();
    }

     scheduleJobs = async () => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var jobs = this.state.results.filter(j => j.selected);

         var schedule = {
            sendEmailToCustomer: this.state.sendEmailToCustomer,
            tiltSlideClearance: this.state.tiltSlideClearance,
            plumbingElectricityDisconnected: this.state.plumbingElectricityDisconnected,
            weekdayAccess: this.state.weekdayAccess,
            phoneCallRequired: this.state.phoneCallRequired,
            jobPickedUp: this.state.jobPickedUp,
            createFinalInvoice: this.state.createFinalInvoice,
            jobPickedUpNotes: this.state.jobPickedUpNotes,
            disconnectionDate: this.state.disconnectionDate,
            fencingPartPickup: this.state.fencePartPickup,
            fencingPartPickupPanels: this.state.fencePartPickupPanels,
            fencingAttachments: this.state.fencingAttachments,
            fencingAttachmentsDescription: this.state.fencingAttachmentsDescription,
            scheduleItems: jobs,
            subId: user.sub
        };

        axios.post('api/Schedule/SaveSchedulePickup', schedule, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            var pickupId = res.data.pickUpId;
            if (!this.state.jobPickedUp) {
                //need to either email the pickup request or just save it to the job
                this.sendReport("JobPickupRequest", pickupId, user.sub)
            } else {
                toast.success("Job Pickup Scheduled Successfully");
                this.setState({
                    pickupConfirmJobIds: [], showPickupConfirm: false, saveDisabled: false, jobIdSearch: "",
                    siteAddressSearch: "", customerSearch: "", showResults: false, disconnectionDate: "",
                    pickupTravelPriceNote: "", pickupTravelExtraHoursPriceNote: "", results: [],
                    tiltSlideClearance: -1, plumbingElectricityDisconnected: -1, phoneCallRequired: -1, weekdayAccess: -1,  
                    jobPickedUp: -1, fencePartPickup: -1, fencePartPickupPanels: 0, fencingAttachments: false,
                    fencingAttachmentsDescription: "", createFinalInvoice: true, jobPickedUpNotes: "",
                    fencePartPickupFee: 0, fencePartPickupFeeFormatted: "$0.00", sendEmailToCustomer: true, errors: {}
                });
            }
        })
        .catch(error => {
            this.setState({ pickupConfirmJobIds: [], showPickupConfirm: false, saveDisabled: false });
            toast.error(error.message);
        });
    }

    sendReport = async (reportName: string, pickupId: number, subId: string) => {
        const token = await authService.getAccessToken();

        //get the data
        axios.get('api/Report/GetJobPickupRequestData?pickupId=' + pickupId + "&reportName=" + reportName, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                errors.map(function (error: any) {
                    toast.error(error.content);
                });
            } else {
                //Run Job Request / Quote Request report
                var report = new Stimulsoft.Report.StiReport();
                var reportFile = Stimulsoft.System.IO.File.getFile("reports/" + res.data.reportFileName);
                report.load(reportFile);
                report.dictionary.dataStore.clear();

                var reportData = JSON.stringify(res.data.reportData);
                //Dataset created
                var dataSet = new Stimulsoft.System.Data.DataSet("data");
                dataSet.readJson(reportData)
                report.regData(dataSet.dataSetName, "", dataSet);
                report.reportName = res.data.reportName;
                report.dictionary.connect(false);
                report.dictionary.synchronize();

                //add relationships between data
                for (var i = 0; i < res.data.reportRelationships.length; i++) {
                    var dataRelation = new Stimulsoft.Report.Dictionary.StiDataRelation(res.data.reportRelationships[i].sourceName, res.data.reportRelationships[i].name, res.data.reportRelationships[i].alias, report.dictionary.dataSources.getByName(res.data.reportRelationships[i].parentTableName), report.dictionary.dataSources.getByName(res.data.reportRelationships[i].childTableName), [res.data.reportRelationships[i].parentFieldName], [res.data.reportRelationships[i].childFieldName]);
                    report.dictionary.relations.add(dataRelation);
                    report.dictionary.synchronize();
                }
                report.render();

                //Download the report to a pdf file
                var service = new Stimulsoft.Report.Export.StiPdfExportService();
                var stream = new Stimulsoft.System.IO.MemoryStream();
                var settings = new Stimulsoft.Report.Export.StiPdfExportSettings();
                service.exportTo(report, stream, settings);
                var bytes = stream.toArray();
    
                var fileData = [new Uint8Array(bytes)];
                var fileName = "Pickup Request";
                var saveFile = new File(fileData, fileName, { type: 'application/pdf' });

                //Send the report to the back-end to send the email to the customer
                const data = new FormData()
                data.append('tempPickupId', pickupId.toString());
                data.append('sendEmail', this.state.sendEmailToCustomer.toString());
                data.append('subId', subId);
                data.append('report', saveFile);
                
                //send report in an email
                axios.post('api/Job/SendPickupConfirmationEmail', data, {
                    headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
                })
                .then(res => {
                    if (res.data.isError) {
                        var errors = res.data.messages as any[];
                        errors.map(function (error: any) {
                            toast.error(error.content);
                        });
                    } else {
                        var emailMessage = " and Emailed Sent to Customer Successfully";
                        toast.success("Job Pickup Saved" + (this.state.sendEmailToCustomer ? emailMessage : ""));
                        this.setState({
                            pickupConfirmJobIds: [], showPickupConfirm: false, saveDisabled: false, jobIdSearch: "",
                            siteAddressSearch: "", customerSearch: "", showResults: false, disconnectionDate: "",
                            pickupTravelPriceNote: "", pickupTravelExtraHoursPriceNote: "", results: [],
                            tiltSlideClearance: -1, plumbingElectricityDisconnected: -1, phoneCallRequired: -1, weekdayAccess: -1,
                            jobPickedUp: -1, fencePartPickup: -1, fencePartPickupPanels: 0, fencingAttachments: false,
                            fencingAttachmentsDescription: "", createFinalInvoice: true, jobPickedUpNotes: "",
                            fencePartPickupFee: 0, fencePartPickupFeeFormatted: "$0.00", sendEmailToCustomer: true, errors: {}
                        });
                    }
                })
                .catch(error => {
                    toast.error(error.message);
                });
            }
        });

    }

    public render() {
        let contents = this.state.loading
            ? <p><em>Loading...</em></p>
            : this.renderData();

        return <div>
            <h1>Schedule Pickup</h1>
            {contents}
        </div>;
    }

    renderData = () => {
        var minDate = DateHelpers.addDaysToCurrentDate(-14);
        var notes = <NotesPopup showEditNotesModal={this.state.showEditNotesModal} editNotes={this.state.editNotes} hideNotes={this.hideNotes} updateNotes={this.updateNotes} saveNotes={this.saveNotes} />
        let schedPickupConfirm = <SchedulePickupConfirmData showModal={this.state.showPickupConfirm} confirmJobIds={this.state.pickupConfirmJobIds} hideModal={this.hideConfirmPickup} saveModal={this.saveConfirmPickup} />
        let truckOptions = this.state.truckOptions;
        let yesNoOptions = this.state.yesNoOptions;
        let results = this.state.results;
        let selectedJobs = results.filter(q => q.selected);

        let isEwsItem = selectedJobs.filter(q => q.isEws && q.itemTypeId != 21).length > 0;
        let isTiltSlide = selectedJobs.filter(q => q.truck === 1).length > 0;
        let isPowerPole = selectedJobs.filter(q => q.itemTypeId === 23).length > 0;
        let isFencing = selectedJobs.filter(q => q.itemTypeId === 50).length > 0;
        let isCreditCard = selectedJobs.filter(q => q.customerCreditCard).length > 0;
        let isDirectDebit = selectedJobs.filter(q => q.customerDirectDebit).length > 0;
        let canSendEmailToCustomer = selectedJobs.length > 0 ? selectedJobs[0].customerSendConfirmationEmail : true;    //customer has to be the same when picking up items so use first item

        return (
            <div>
                {notes}
                {schedPickupConfirm}
                <div className="makeitflex">
                    <label className="input-group--inline" htmlFor="jobIdSearch">
                        <span className="label">Job Id</span>
                        <input className="input schedulenew__addSearch--width-sm" type="text" id="jobIdSearch" name="jobIdSearch" value={this.state.jobIdSearch} onChange={(e) => this.handleChange(e)} onKeyUp={(e) => this.onKeyUp(e)}></input>
                    </label>
                    <label className="input-group--inline" htmlFor="siteAddressSearch">
                        <span className="label label--setsizesm">Site Address</span>
                        <input className="input schedulenew__addSearch--width-lg" type="text" id="siteAddressSearch" name="siteAddressSearch" value={this.state.siteAddressSearch} onChange={(e) => this.handleChange(e)} onKeyUp={(e) => this.onKeyUp(e)}></input>
                    </label>
                    <label className="input-group--inline" htmlFor="customerSearch">
                        <span className="label label--setsizesm">Customer</span>
                        <input className="input schedulenew__addSearch--width-lg" type="text" id="customerSearch" name="customerSearch" value={this.state.customerSearch} onChange={(e) => this.handleChange(e)} onKeyUp={(e) => this.onKeyUp(e)}></input>
                    </label>
                    <button className="defaultbutton defaultbutton__small allocate__searchMargin" type='button' onClick={(e) => this.search(e)}>Search</button>
                    <button className="hidden" type='button' onClick={(e) => this.selectJobs(e)}>SelectAllJobs</button>
                </div>
                <div className={this.state.showResults ? "" : "hidden"}>
                    <div className={this.state.results.length > 0 ? '' : 'hidden'}>
                        <table className='table tableColours'>
                            <thead>
                                <tr>
                                    <th className="hidden">Job Quote Item Id</th>
                                    <th></th>
                                    <th></th>
                                    <th>Job Id</th>
                                    <th>Site Address</th>
                                    <th>Customer</th>
                                    <th>P/O No</th>
                                    <th>Item</th>
                                    <th>Truck</th>
                                    <th># On Site</th>
                                    <th>Quantity</th>
                                    <th>Hire Period</th>
                                    <th>Pick Up Date</th>
                                    <th>Offhire Date</th>
                                    <th>Bring Forward</th>
                                    <th>Notes</th>
                                </tr>
                            </thead>
                            <tbody>
                                {results.map((result, index) =>
                                    <tr key={index}>
                                        <td className="hidden">{result.jobQuoteItemId}</td>
                                        <td className="table__text--align">{result.itemIndex + 1}</td>
                                        <td className="table__text--align"><input type='checkbox' className="input" checked={result.selected} onChange={(e) => this.selectItem(e, index)} /></td>
                                        <td className="table__text--align">
                                            <Link to={'/job/' + result.jobId} target="_blank" >
                                                {result.jobId}
                                            </Link>
                                        </td>
                                        <td className="table__text--align">
                                            <div className="tooltipitem  tooltipitem__right">{result.siteAddress ? result.siteAddress.substring(0, 30) + "... " : ""}
                                                <span className="tooltipitemtext  tooltipitemtext__right">{result.siteAddress}</span>
                                            </div>
                                        </td>
                                        <td className="table__text--align">{result.customer}</td>
                                        <td className="table__text--align">{result.purchaseOrderNumber}</td>
                                        <td className="table__text--align">
                                            <div className="tooltipitem tooltipitem__right">{result.itemShortcut}
                                                <span className="tooltipitemtext tooltipitemtext__right">{result.item}</span>
                                            </div>
                                        </td>
                                        <td className="table__text--align">
                                            <select className={result.canChangeTruck ? "select" : "hidden"} id="truck" name="truck" value={result.truck} onChange={(e) => this.onCellTruckChange(e, index)}>
                                                <option hidden defaultValue="-1" value={-1}></option>
                                                {truckOptions.map(truck =>
                                                    <option key={truck.id} value={truck.id}>{truck.name}</option>
                                                )};
                                            </select>
                                        </td>
                                        <td className="table__text--align">
                                            {result.quantityScheduledAvailable}
                                        </td>
                                        <td className="table__text--align">
                                            <input type='number' min={1} step={1} id="quantityScheduled" name="quantityScheduled" className={result.quantityScheduledAvailable > 1 ? "form-control allocate__qtySize" : "hidden"} value={result.quantityScheduled} onChange={(e) => this.onCellChange(e, index)} />
                                            {result.quantityScheduledAvailable > 1 ? "" : result.quantityType}
                                        </td>
                                        <td className="table__text--align">{result.hirePeriod}</td>
                                        <td className="table__text--align">
                                            <input type="date" id="pickupDate" name="pickupDate" className="form-control" value={result.pickupDate} onChange={(e) => this.onCellChange(e, index)} />
                                        </td>
                                        <td className="table__text--align">
                                            <input type='date' id="offhireDate" name="offhireDate" className="form-control" value={result.offhireDate} onChange={(e) => this.onCellChange(e, index)} />
                                        </td> 
                                        <td className="table__text--align">
                                            <input type='date' min={minDate} id="bringForwardDate" name="bringForwardDate" className="form-control" value={result.bringForwardDate} onChange={(e) => this.onCellChange(e, index)} />
                                        </td> 
                                        <td className="table__text--align allocate-notes--width">
                                            <a className={result.notes == "" ? "hidden" : ""} data-toggle="modal" data-target="#notesModal" href="#" onClick={(e) => this.editNotes(e, index)}>
                                                <div className="tooltipitem tooltipitem__left">{result.notes ? result.notes.length < 60 ? result.notes : result.notes.substring(0, 60) + "... " : ""}
                                                    <span className="tooltipitemtext tooltipitemtext__large tooltipitemtext__left">{result.notes}</span>
                                                </div>
                                            </a>
                                            <a className={result.notes != "" ? "hidden" : ""} data-toggle="modal" data-target="#notesModal" href="#" onClick={(e) => this.editNotes(e, index)}>
                                                <span className="fas fa-pencil-alt edit--icon alignIconCenter"></span>
                                            </a>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    {this.state.errors["scheduleItems"] ?
                        (this.state.errors["scheduleItems"]).split("\n").map((item: any, key: any) => {
                            return <span className="label errors" key={key}>{item}<br /></span>
                        })
                        : ""}
                    <span className={this.state.errors["multipleJobIds"] ? "label errors marginLeftLabelLarge" : "hidden"}>{this.state.errors["multipleJobIds"]}</span>

                    <div className={isEwsItem && isTiltSlide ? "makeitred" : "hidden"}>Note: If Tilt Truck used for dropoff but Crane Truck required for pickup there will be an extra cost of {this.state.pickupTravelPriceNote}</div>
                    <div className={isEwsItem && isTiltSlide ? "makeitred" : "hidden"}>Any travel over 3 hours will be charged at {this.state.pickupTravelExtraHoursPriceNote} per hour</div>

                    <div className={this.state.results.length == 0 ? "hidden" : ""}>
                        <label className="input-group marginBottom10" htmlFor="jobPickedUp">
                            <span className="label-large">Item already picked up from site?<i className="fa-regular fa-circle-question edit--icon marginLeft10" title="Pickup will be created and completed without going through Logistics."></i></span>
                            <select className="select" id="jobPickedUp" name="jobPickedUp" value={this.state.jobPickedUp} onChange={(e) => this.handleJobPickedUpChange(e)}>
                                <option hidden defaultValue="-1"></option>
                                {yesNoOptions.map(yn =>
                                    <option key={yn.id} value={yn.id}>{yn.name}</option>
                                )};
                            </select>
                        </label>
                        <span className={this.state.jobPickedUp === 1 ? "marginLeftLabelLarge makeitred" : "hidden"}>Pickup Job WILL NOT go to Logistics</span>
                        <span className={this.state.errors["jobPickedUp"] ? "label errors marginLeftLabelLarge" : "hidden"}>{this.state.errors["jobPickedUp"]}</span>

                        <label className={this.state.jobPickedUp === 1 ? "input-group marginBottom10" : "hidden"} htmlFor="createFinalInvoice">
                            <span className="label-large">Create Final Invoice (if required)?<i className="fa-regular fa-circle-question edit--icon marginLeft10" title="If unticked, NO final invoice will be created. Note: PART Pickup will NOT create a Final Invoice"></i></span>
                            <input className="checkbox" type="checkbox" id="createFinalInvoice" name="createFinalInvoice" checked={this.state.createFinalInvoice} onChange={(e) => this.handleFinalInvoiceCheckboxChange(e)}></input>
                        </label>

                        <label className={this.state.jobPickedUp === 1 ? "input-group marginBottom10" : "hidden"} htmlFor="jobPickedUpNotes">
                            <span className="label-large">Job Picked Up Notes</span>
                            <textarea className="input allocate__teamnotes" id="jobPickedUpNotes" name="jobPickedUpNotes" value={this.state.jobPickedUpNotes} onChange={(e) => this.handlejobPickedUpNotesChange(e)}></textarea>
                        </label>
                        <span className={this.state.errors["jobPickedUpNotes"] ? "label errors marginLeftLabelLarge" : "hidden"}>{this.state.errors["jobPickedUpNotes"]}</span>
                    </div>

                    <div className={this.state.jobPickedUp < 1 ? "" : "hidden"}>
                        <label className={this.state.results.length == 0 ? "hidden" : "input-group marginBottom10"} htmlFor="sendEmailToCustomer">
                            <span className="label-large">Send Confirmation Email</span>
                            <input className="checkbox" type="checkbox" id="sendEmailToCustomer" name="sendEmailToCustomer" checked={this.state.sendEmailToCustomer} onChange={(e) => this.handleSendEmailCheckboxChange(e)} disabled={!canSendEmailToCustomer}></input>
                        </label>
                        <div className={isEwsItem ? "" : "hidden"}>
                            <label className={isTiltSlide ? "input-group marginBottom10" : "hidden"} htmlFor="tiltSlideClearance">
                                <span className="label-large">Is there 17m clearance for Tilt Slide Truck?</span>
                                <select className="select" id="tiltSlideClearance" name="tiltSlideClearance" value={this.state.tiltSlideClearance} onChange={(e) => this.handleTruckClearanceChange(e)}>
                                    <option hidden defaultValue="-1"></option>
                                    {yesNoOptions.map(yn =>
                                        <option key={yn.id} value={yn.id}>{yn.name}</option>
                                    )};
                                </select>
                            </label>
                            <span className={this.state.errors["tiltSlideClearance"] ? "label errors marginLeftLabelLarge" : "hidden"}>{this.state.errors["tiltSlideClearance"]}</span>
                        </div>
                        <div className={isEwsItem || isPowerPole ? "" : "hidden"}>
                            <label className="input-group marginBottom10" htmlFor="plumbingElectricityDisconnected">
                                <span className="label-large">{isEwsItem ? "Has the plumbing and/or electricity been disconnected?" : "Has the electricity been disconnected?"}</span>
                                <select className="select" id="plumbingElectricityDisconnected" name="plumbingElectricityDisconnected" value={this.state.plumbingElectricityDisconnected} onChange={(e) => this.handleChangeNumber(e)}>
                                    <option hidden defaultValue="-1"></option>
                                    {yesNoOptions.map(yn =>
                                        <option key={yn.id} value={yn.id}>{yn.name}</option>
                                    )};
                                </select>
                            </label>
                            <span className={this.state.errors["plumbingElectricityDisconnected"] ? "label errors marginLeftLabelLarge" : "hidden"}>{this.state.errors["plumbingElectricityDisconnected"]}</span>
                            <span className={isPowerPole && this.state.plumbingElectricityDisconnected === 0 ? "label errors" : "hidden"}>Customer needs to get the electricity disconnected before we can arrange pickup of the power pole!</span>
                            <span className={isPowerPole ? "label errors" : "hidden"}>Note 1 : Let customer know there will be a Callout Fee charged if the electricity hasn't been disconnected</span>
                            <span className={isPowerPole ? "label errors" : "hidden"}>Note 2: Ask the customer to email us a photo of the power pole</span>

                            <div className={isPowerPole && this.state.plumbingElectricityDisconnected === 1 ? "" : "hidden"}>
                                <label className="input-group marginBottom10" htmlFor="disconnectionDate">
                                    <span className="label-large">Disconnection Date</span>
                                    <input type='date' id="disconnectionDate" name="disconnectionDate" className='input' value={this.state.disconnectionDate} onChange={(e) => this.handleChange(e)}></input>
                                </label>
                                <span className={this.state.errors["disconnectionDate"] ? "label errors marginLeftLabelLarge" : "hidden"}>{this.state.errors["disconnectionDate"]}</span>
                            </div>
                        </div>

                        <div className={isFencing ? "" : "hidden"}>
                            <label className="input-group marginBottom10" htmlFor="fencePartPickup">
                                <span className="label-large">Is this a part pickup?</span>
                                <select className="select" id="fencePartPickup" name="fencePartPickup" value={this.state.fencePartPickup} onChange={(e) => this.handlePartPickupChange(e)}>
                                    <option hidden defaultValue="-1"></option>
                                    {yesNoOptions.map(yn =>
                                        <option key={yn.id} value={yn.id}>{yn.name}</option>
                                    )};
                                </select>
                            </label>
                            <div className={this.state.fencePartPickup === 1 && !isCreditCard && !isDirectDebit ? "marginLeftLabelLarge makeitred" : "hidden"}>Customer will be charged {this.state.fencePartPickupFee > 0 ? this.state.fencePartPickupFeeFormatted : ""} for a part pickup</div>
                            <div className={this.state.fencePartPickup === 1 && isCreditCard ? "marginLeftLabelLarge makeitred" : "hidden"}>Charge Customers Credit Card {this.state.fencePartPickupFee > 0 ? this.state.fencePartPickupFeeFormatted : ""} for part pickup</div>
                            <div className={this.state.fencePartPickup === 1 && isDirectDebit ? "marginLeftLabelLarge makeitred" : "hidden"}>Customer needs to transfer {this.state.fencePartPickupFee > 0 ? this.state.fencePartPickupFeeFormatted : ""} via Direct Debit for part pickup</div>
                            <span className={this.state.errors["fencePartPickup"] ? "label errors marginLeftLabelLarge" : "hidden"}>{this.state.errors["fencePartPickup"]}</span>

                            <label className={this.state.fencePartPickup === 1 ? "input-group" : "hidden"} htmlFor="fencePartPickupPanels">
                                <span className="label-large">Estimated # Panels (use 0 if unknown)</span>
                                <input className="input" type="text" id="fencePartPickupPanels" name="fencePartPickupPanels" value={this.state.fencePartPickupPanels} onChange={(e) => this.handleChangeNumber(e)}></input>
                            </label>

                            <label className="input-group marginBottom10" htmlFor="fencingAttachments">
                                <span className="label-large">Are there any banners / signs / locks that need to be removed from the fence?</span>
                                <input className="checkbox" type="checkbox" name="fencingAttachments" checked={this.state.fencingAttachments} onChange={(e) => this.handleFencingAttachmentsChange(e)} ></input>
                            </label>
                            <span className={this.state.errors["fencingAttachments"] ? "label errors marginLeftLabelLarge" : "hidden"}>{this.state.errors["fencingAttachments"]}</span>

                            <label className={this.state.fencingAttachments ? "input-group" : "hidden"} htmlFor="fencingAttachmentsDescription">
                                <span className="label-large">Enter items that need to be removed </span>
                                <input className="input" type="text" id="fencingAttachmentsDescription" name="fencingAttachmentsDescription" value={this.state.fencingAttachmentsDescription} onChange={(e) => this.handleChange(e)}></input>
                            </label>
                            <span className={this.state.errors["fencingAttachmentsDescription"] ? "label errors marginLeftLabelLarge" : "hidden"}>{this.state.errors["fencingAttachmentsDescription"]}</span>
                        </div>

                        <div className={this.state.results.length == 0 ? "hidden" : ""}>
                            <label className="input-group marginBottom10" htmlFor="weekdayAccess">
                                <span className="label-large">Is there clear access between 7am and 3pm weekdays?</span>
                                <select className="select" id="weekdayAccess" name="weekdayAccess" value={this.state.weekdayAccess} onChange={(e) => this.handleChangeNumber(e)}>
                                    <option hidden defaultValue="-1"></option>
                                    {yesNoOptions.map(yn =>
                                        <option key={yn.id} value={yn.id}>{yn.name}</option>
                                    )};
                                </select>
                            </label>
                            <span className={this.state.errors["weekdayAccess"] ? "label errors marginLeftLabelLarge" : "hidden"}>{this.state.errors["weekdayAccess"]}</span>

                            <label className="input-group marginBottom10" htmlFor="phoneCallRequired">
                                <span className="label-large">Phone call required prior to arrival?</span>
                                <select className="select" id="phoneCallRequired" name="phoneCallRequired" value={this.state.phoneCallRequired} onChange={(e) => this.handleChangeNumber(e)}>
                                    <option hidden defaultValue="-1"></option>
                                    {yesNoOptions.map(yn =>
                                        <option key={yn.id} value={yn.id}>{yn.name}</option>
                                    )};
                                </select>
                            </label>
                            <span className={this.state.errors["phoneCallRequired"] ? "label errors marginLeftLabelLarge" : "hidden"}>{this.state.errors["phoneCallRequired"]}</span>
                        </div>
                    </div>

                    <span className={this.state.results.length == 0 ? "" : "hidden"}>There were no matching jobs found. Try modifying your search criteria.</span>
                    <div className={this.state.results.length == 0 ? "hidden" : "colAlignRight marginBottom10"}>
                        <button className="defaultbutton defaultbutton__large" type="button" onClick={this.schedule} disabled={this.state.saveDisabled}>Schedule</button>
                    </div>
                </div>
                
            </div>


        );
    }

}